import React, { useState } from 'react';

const MOCK_CARDS = [
  {
    id: 'card1',
    type: 'Debit Card',
    number: '**** **** **** 6874',
    expiry: '12/27',
    status: 'Active',
    linked: 'Checking',
    design: 'Standard',
    lastTransactions: [
      { date: '2025-03-15', description: 'Grocery Store', amount: -84.52 },
      { date: '2025-03-14', description: 'Gas Station', amount: -45.00 },
      { date: '2025-03-13', description: 'Coffee Shop', amount: -4.75 }
    ]
  },
  {
    id: 'card2',
    type: 'Credit Card',
    number: '**** **** **** 2478',
    expiry: '03/28',
    status: 'Active',
    limit: 5000,
    available: 3500,
    rewards: 2500,
    design: 'Platinum',
    lastTransactions: [
      { date: '2025-03-15', description: 'Restaurant', amount: -125.00 },
      { date: '2025-03-14', description: 'Online Shopping', amount: -89.99 },
      { date: '2025-03-13', description: 'Streaming Service', amount: -14.99 }
    ]
  }
];

const STATEMENT_PERIODS = [
  { id: '2025-01', label: 'January 2025' },
  { id: '2025-12', label: 'December 2024' },
  { id: '2024-11', label: 'November 2024' },
  { id: '2024-10', label: 'October 2024' },
  { id: '2024-09', label: 'September 2024' },
  { id: '2024-08', label: 'August 2024' },
  { id: '2024-07', label: 'July 2024' },
  { id: '2024-06', label: 'June 2024' },
  { id: '2024-05', label: 'May 2024' },
  { id: '2024-04', label: 'April 2024' },
  { id: '2024-03', label: 'March 2024' },
  { id: '2024-02', label: 'February 2024' },
  { id: '2024-01', label: 'January 2024' }
];

export default function CardServices() {
  const [activeTab, setActiveTab] = useState('overview');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpAction, setOtpAction] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [cards, setCards] = useState(MOCK_CARDS);

  const handleFreezeCard = (cardId) => {
    setCards(prevCards =>
      prevCards.map(card =>
        card.id === cardId
          ? { ...card, status: 'Frozen' }
          : card
      )
    );
  };

  const handleUnfreezeCard = (cardId) => {
    setCards(prevCards =>
      prevCards.map(card =>
        card.id === cardId
          ? { ...card, status: 'Active' }
          : card
      )
    );
  };

  const handleReplaceCard = (cardId) => {
    // Show confirmation modal or directly trigger card replacement process
    handleActionClick('replace');
  };

  const handleActionClick = (action) => {
    setOtpAction(action);
    setShowOtpModal(true);
    setOtp('');
    setOtpError('');
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    setOtpError('Service temporarily unavailable. Please try again later.');
  };

  const getActionTitle = () => {
    switch (otpAction) {
      case 'pin':
        return 'Change PIN';
      case 'limits':
        return 'Set Transaction Limits';
      case 'details':
        return 'View Card Details';
      default:
        return '';
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Card Services</h3>

      {/* Card Navigation */}
      <div className="flex space-x-2 mb-6 bg-gray-50 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200
            ${activeTab === 'overview'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'}`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('statements')}
          className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200
            ${activeTab === 'statements'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'}`}
        >
          Statements
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-8">
          {cards.map((card) => (
            <div key={card.id} className="flex flex-col lg:flex-row gap-6">
              {/* Card Column */}
              <div className="w-full lg:w-[340px] flex-shrink-0">
                <div 
                  className="group relative"
                  onMouseEnter={() => setHoveredCard(card.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Card Container with 3D effect */}
                  <div className={`relative transition-all duration-500 ease-out transform
                    ${hoveredCard === card.id ? 'scale-105 -rotate-1' : 'scale-100 rotate-0'}`}
                  >
                    {/* Main Card */}
                    <div className={`relative z-10 bg-gradient-to-br 
                      ${card.type === 'Credit Card' 
                        ? 'from-primary-600 via-primary-700 to-primary-800'
                        : 'from-emerald-500 via-emerald-600 to-emerald-700'} 
                      rounded-2xl p-5 shadow-lg overflow-hidden w-full lg:w-[340px] aspect-[1.586]`}
                    >
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-white/20 blur-2xl transform -rotate-12"></div>
                        <div className="absolute -left-16 -bottom-16 w-48 h-48 rounded-full bg-black/20 blur-2xl"></div>
                      </div>

                      {/* Card Content */}
                      <div className="relative h-full flex flex-col justify-between">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-base font-semibold text-white mb-1">{card.type}</h3>
                            <p className="text-white/80 text-xs">{card.design}</p>
                          </div>
                          <span className={`px-2.5 py-1 rounded-full text-xs backdrop-blur-sm
                            ${card.status === 'Active' 
                              ? 'bg-green-400/20 text-green-100' 
                              : 'bg-yellow-400/20 text-yellow-100'}`}
                          >
                            {card.status}
                          </span>
                        </div>
              
                        <div className="my-4">
                          <p className="text-xl font-mono text-white tracking-wider mb-1">{card.number}</p>
                          <p className="text-xs text-white/80">Expires: {card.expiry}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {card.type === 'Credit Card' ? (
                            <>
                              <div>
                                <p className="text-xs text-white/80">Credit Limit</p>
                                <p className="text-sm font-semibold text-white">${card.limit.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-white/80">Available</p>
                                <p className="text-sm font-semibold text-white">${card.available.toFixed(2)}</p>
                              </div>
                            </>
                          ) : (
                            <div>
                              <p className="text-xs text-white/80">Linked Account</p>
                              <p className="text-sm font-semibold text-white">{card.linked}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div className={`mt-3 flex space-x-2 transition-all duration-500 transform
                      ${hoveredCard === card.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    >
                      <button className="flex-1 bg-white hover:bg-gray-50 text-gray-900 py-2 px-3 rounded-xl 
                        shadow-sm transition-all duration-200 flex items-center justify-center space-x-1 text-sm">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span>Lock Card</span>
                      </button>
                      <button className="flex-1 bg-white hover:bg-gray-50 text-gray-900 py-2 px-3 rounded-xl 
                        shadow-sm transition-all duration-200 flex items-center justify-center space-x-1 text-sm">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>Report Lost</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Details Column */}
              <div className="flex-1 space-y-4">
                {/* Quick Actions */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <button 
                      onClick={() => handleActionClick('pin')}
                      className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-50 
                      py-2 px-3 rounded-lg text-sm text-gray-700 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span>Change PIN</span>
                    </button>
                    <button 
                      onClick={() => handleActionClick('limits')}
                      className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-50 
                      py-2 px-3 rounded-lg text-sm text-gray-700 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Set Limits</span>
                    </button>
                    <button 
                      onClick={() => handleActionClick('details')}
                      className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-50 
                      py-2 px-3 rounded-lg text-sm text-gray-700 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span>Get Details</span>
                    </button>
                  </div>
                </div>

                {/* Recent Transactions */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Transactions</h4>
                  <div className="space-y-2">
                    {card.lastTransactions.map((transaction, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{transaction.description}</p>
                          <p className="text-xs text-gray-500">{transaction.date}</p>
                        </div>
                        <span className={`text-sm font-medium ml-4 ${transaction.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ${Math.abs(transaction.amount).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {card.type === 'Credit Card' && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Credit Usage</h4>
                    <div className="space-y-2">
                      <div className="w-full bg-white rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Current Usage</span>
                          <span className="text-sm font-medium text-gray-900">
                            ${(card.limit - card.available).toFixed(2)} of ${card.limit.toFixed(2)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div 
                            className="h-full bg-primary-600 rounded-full transition-all duration-500"
                            style={{ width: `${(1 - card.available/card.limit) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center bg-white p-3 rounded-lg">
                        <span className="text-sm text-gray-600">Rewards Points</span>
                        <span className="text-sm font-medium text-gray-900">{card.rewards.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'statements' && (
        <div className="space-y-4">
          {STATEMENT_PERIODS.map((period) => (
            <div 
              key={period.id} 
              className="flex justify-between items-center p-4 bg-gray-50 hover:bg-primary-50 rounded-xl transition-all duration-200 group cursor-pointer"
            >
              <div className="flex items-center space-x-3">
                <span className="text-xl">📄</span>
                <span className="text-gray-900">{period.label}</span>
              </div>
              <button className="text-primary-600 opacity-0 group-hover:opacity-100 transition-all duration-200">
                Download →
              </button>
            </div>
          ))}
        </div>
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative animate-fadeIn">
            {/* Close button */}
            <button 
              onClick={() => setShowOtpModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal content */}
            <div className="mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{getActionTitle()}</h3>
                  <p className="text-sm text-gray-600">Enter the OTP sent to your registered mobile number</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  One-Time Password (OTP)
                </label>
                <input
                  type="text"
                  maxLength="6"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-primary-500 focus:border-primary-500 
                    bg-gray-50 hover:bg-white transition-colors duration-200 text-center text-lg tracking-wider"
                  placeholder="Enter 6-digit OTP"
                />
              </div>

              {otpError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {otpError}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowOtpModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 
                    transition-colors duration-200"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 
                    transition-colors duration-200"
                >
                  Verify
                </button>
              </div>

              <div className="text-center">
               
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 